import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { message, Form } from 'antd';
import AuthService from 'services/Auth';
import WebQrsApi from 'libs/api/website/webQrs';

const useWebQrs = () => {
  const [webQrs, setWebQrs] = useState([]);
  const [singleItem, setSingleItem] = useState({});
  const [loading, setLoading] = useState(false);
  const [updateInProcess, setUpdateInProcess] = useState(false);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [filters, setFilters] = useState({});
  const [shoppingCenters, setShoppingCenters] = useState([]);
  const [loadingCompanies, setLoadingCompanies] = useState(false);

  const navigate = useNavigate();
  const { id } = useParams();
  const [form] = Form.useForm();
  const paramsRef = useRef({});

  const checkAuth = useCallback(() => {
    const token = AuthService.getToken();
    if (!token) {
      message.error('You are not authorized. Please login.');
      navigate('/login');
      return false;
    }
    return true;
  }, [navigate]);


  const fetchWebQrs = useCallback(async (params = {}) => {
    if (!checkAuth()) return;

    setLoading(true);
    try {
      const response = await WebQrsApi.list({
        page: params.page || currentPage,
        per: params.per || pageSize,
        ...filters,
        ...params,
      });

      if (response.data.success) {
        setWebQrs(response.data.web_qrs || []);
        setTotal(response.data.pagination?.total_count || 0);
        setCurrentPage(response.data.pagination?.current || 1);
      }
    } catch (error) {
      message.error('Errore nel caricamento dei QR codes');
      console.error('Error fetching web QRs:', error);
    } finally {
      setLoading(false);
    }
  }, [checkAuth, currentPage, pageSize, filters]);


  const fetchSingleWebQr = useCallback(async (qrId) => {
    if (!checkAuth() || !qrId) return;

    setLoading(true);
    try {
      const response = await WebQrsApi.show(qrId);
      
      if (response.data.success) {
        setSingleItem(response.data.web_qr || {});
        

        const qrData = response.data.web_qr;
        const formValues = {
          link: qrData?.link || '',
          qr_link: qrData?.qr_link || '',
          slug: qrData?.slug || '',
          count_open: qrData?.count_open || 0,
          shopping_center_id: qrData?.shopping_center?.id || null,
          link_type: qrData?.qr_link ? 'link' : qrData?.file ? 'file' : 'link',
        };


        if (qrData?.file) {
          formValues.file = [{
            uid: '-1',
            name: qrData.file_name || 'file',
            status: 'done',
            url: typeof qrData.file === 'string' ? qrData.file : qrData.file?.url || qrData.file
          }];
        }

        form.setFieldsValue(formValues);
      }
    } catch (error) {
      message.error('Errore nel caricamento del QR code');
      console.error('Error fetching single web QR:', error);
    } finally {
      setLoading(false);
    }
  }, [checkAuth, form]);


  const handleCreateOrUpdate = useCallback(async (values) => {
    if (!checkAuth()) return;

    setUpdateInProcess(true);
    try {
      const formData = new FormData();
      
      for (let key in values) {
        if (key === 'file' && values[key] && values[key][0]) {
          formData.append(`web_qr[${key}]`, values[key][0].originFileObj);
        } else if (key !== 'link_type' && values[key] !== undefined && values[key] !== null) {
          formData.append(`web_qr[${key}]`, values[key]);
        }
      }

      if (id) {
   
        await WebQrsApi.update(id, formData);
  
      } else {

        await WebQrsApi.create(formData);
      }
      
      navigate('/website/web_qrs');
    } catch (error) {
      message.error('Errore nel salvare il QR code');
      console.error('Error saving web QR:', error);
    } finally {
      setUpdateInProcess(false);
    }
  }, [checkAuth, id, navigate]);


  const handleDelete = useCallback(async (qrId) => {
    if (!checkAuth()) return;

    try {
      await WebQrsApi.delete(qrId);
      message.success('QR code eliminato con successo');
      fetchWebQrs();
    } catch (error) {
      message.error('Errore nell\'eliminazione del QR code');
      console.error('Error deleting web QR:', error);
    }
  }, [checkAuth, fetchWebQrs]);


  const fetchFilterData = useCallback(async () => {
    if (!checkAuth()) return;

    setLoadingCompanies(true);
    try {
      const response = await WebQrsApi.getFilters();
      const shoppingCentersRaw = response.data.shopping_centers || [];
      setShoppingCenters(shoppingCentersRaw);
    } catch (error) {
      console.error('Error fetching filter data:', error);
      message.error('Failed to load filters');
    } finally {
      setLoadingCompanies(false);
    }
  }, [checkAuth]);


  useEffect(() => {
    fetchFilterData();
  }, [fetchFilterData]);

  useEffect(() => {
    if (id) {
      fetchSingleWebQr(id);
    }
  }, [id, fetchSingleWebQr]);

  useEffect(() => {
    if (!id) {
      fetchWebQrs();
    }
  }, [fetchWebQrs, id]);

  return {
    webQrs,
    singleItem,
    loading,
    updateInProcess,
    total,
    currentPage,
    pageSize,
    filters,
    shoppingCenters,
    loadingCompanies,
    form,
    fetchWebQrs,
    fetchSingleWebQr,
    handleCreateOrUpdate,
    handleDelete,
    setFilters,
    setCurrentPage,
    setPageSize,
  };
};

export default useWebQrs;
